import React, { ErrorInfo, ReactNode } from 'react';
import {
  getUserIdentifier,
  ILogsContext,
  INLINE_SERVICE_NAME,
  MetricEvents,
  myHRLoggerErrorLog,
  PageName,
} from '@amzn/myhr-livehelp-ui';
import { LaasClientId } from '@amzn/myhr-ui-commons';
import { recordMetricForFab } from '../utils/moduleWebsiteLoggerUtils';
import { FAB_RENDERING_OPERATION_NAME } from '../consts/constants';

interface IErrorBoundaryProps {
  children: ReactNode;
  clientId: LaasClientId;
  pageName: string;
}

interface IErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * @function getDerivedStateFromError - function updates state to render Error UI
   * @returns - returns updated state
   */
  public static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * @function componentDidCatch - function to log errors to cloudwatch and redirect user to error page
   * @param error - minor info on error
   * @param errorInfo - detailed info on error
   */
  // eslint-disable-next-line class-methods-use-this
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { clientId, pageName } = this.props;
    const logsContext: ILogsContext = {
      serviceName: INLINE_SERVICE_NAME,
      pageName: PageName.LIVEHELP_INLINE_PAGE_NAME,
      operationName: FAB_RENDERING_OPERATION_NAME,
      userIdentifier: getUserIdentifier(),
    };

    recordMetricForFab(
      logsContext.operationName,
      logsContext.serviceName,
      clientId,
      pageName,
      MetricEvents.ERROR_COUNT,
    );

    myHRLoggerErrorLog(
      `Unable to render FAB in  pageName: ${pageName} for client: ${clientId}, error: ${JSON.stringify(
        error,
      )}, errorInfo: ${JSON.stringify(errorInfo)}`,
      logsContext,
    );
  }

  render() {
    const { hasError } = this.state;
    if (hasError) {
      // When something breaks in Inline Widget, we will show nothing to ensure that the client's page is not broken
      return null;
    }

    const { children } = this.props;

    return children;
  }
}

export default ErrorBoundary;
